// Per photo data of a multi photo bundle: every photo is a small crystal of its own
// with two engraving lines and the options made for it (keychains, ornaments, keep
// background...). An order from the API may also give a photo its own laser engraved
// text and canvas. Stored on the item as a JSON string (extension_attributes.multi_photo_items);
// the upload item list already returns it decoded.

// Bundle option titles of addons that carry a customer photo (same list as the backend).
export const PHOTO_ADDON_LABELS = {
  keychains: "Keychain",
  heart_keychain: "Heart Keychain",
  rectangle_keychain: "Rectangle Keychain",
  square_keychain: "Square Keychain",
  circle_keychain: "Circle Keychain",
  dog_bone_keychain: "Dog Bone Keychain",
  cat_keychain: "Cat Keychain",
  cat_necklace: "Cat Necklace",
  circle_ornament: "Circle Ornament",
  heart_ornament: "Heart Ornament",
  cat_ornament: "Cat Ornament",
  dog_bone_ornament: "Dog Bone Ornament",
  flower_ornament: "Flower Ornament",
  "3d_pop_up_card": "3D Pop Up Card",
};

// Bundle options of the item as a whole, never of one photo (same list as the backend).
export const ITEM_OPTIONS = [
  "size",
  "customer_text",
  "laser_engraved_text",
  // one backdrop for the bundle, made for every photo
  "two_backdrop",
  "three_backdrop",
  "custom_option",
  "design_work",
  "photo_restoration",
  "digi_preview",
  // red_carpet and queue_48 are the selections of this option
  "service_queue",
];

// Values a photo may have on its own, kept only when set.
const OPTIONAL_VALUES = ["laser_engraved_text", "canvas_project_id", "canvas_job_id"];

function optionLabel(code) {
  if (PHOTO_ADDON_LABELS[code]) {
    return PHOTO_ADDON_LABELS[code];
  }
  return String(code)
    .split("_")
    .filter((word) => word)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function emptyMultiPhotoItems(count) {
  let result = [];
  for (let photo = 1; photo <= count; photo++) {
    result.push({ photo: photo, text: "", text_2: "", addons: {} });
  }
  return result;
}

function cleanAddons(addons) {
  let result = {};
  if (!addons || typeof addons !== "object") {
    return result;
  }
  Object.keys(addons).forEach((code) => {
    let qty = parseInt(addons[code], 10);
    if (qty > 0 && !ITEM_OPTIONS.includes(code)) {
      result[code] = qty;
    }
  });
  return result;
}

// Normalizes stored data (JSON string or array) to exactly `count` entries;
// without a count, keeps whatever photos are present.
export function parseMultiPhotoItems(value, count = null) {
  let raw = value;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      raw = [];
    }
  }
  if (!Array.isArray(raw)) {
    raw = [];
  }
  let total = count || raw.length;
  let result = emptyMultiPhotoItems(total);
  raw.forEach((entry, index) => {
    if (!entry || typeof entry !== "object") {
      return;
    }
    let photo = parseInt(entry.photo, 10) || index + 1;
    if (photo < 1 || photo > total) {
      return;
    }
    result[photo - 1].text = entry.text ? String(entry.text) : "";
    result[photo - 1].text_2 = entry.text_2 ? String(entry.text_2) : "";
    result[photo - 1].addons = cleanAddons(entry.addons);
    OPTIONAL_VALUES.forEach((key) => {
      if (entry[key] !== undefined && entry[key] !== null && String(entry[key]).trim() !== "") {
        result[photo - 1][key] = String(entry[key]).trim();
      }
    });
  });
  return result;
}

// Entries that have any text or option, ready to display:
// { photo, lines: [...], addons: [{ code, label, qty }], giftNote, summary }.
export function filledMultiPhotoItems(value) {
  return parseMultiPhotoItems(value)
    .map((entry) => {
      let lines = [entry.text, entry.text_2].filter((line) => line);
      let addons = Object.keys(entry.addons).map((code) => ({
        code: code,
        label: optionLabel(code),
        qty: entry.addons[code],
      }));
      let giftNote = entry.laser_engraved_text || "";
      let parts = [];
      if (lines.length) {
        parts.push(lines.join(" / "));
      }
      addons.forEach((addon) => {
        parts.push(`${addon.label} ×${addon.qty}`);
      });
      if (giftNote) {
        parts.push(`Gift Note: ${giftNote}`);
      }
      return {
        photo: entry.photo,
        lines: lines,
        addons: addons,
        giftNote: giftNote,
        summary: parts.join(" · "),
      };
    })
    .filter((entry) => entry.lines.length || entry.addons.length || entry.giftNote);
}

export function hasMultiPhotoContent(value) {
  return filledMultiPhotoItems(value).length > 0;
}

// Whether any photo has an engraving (its addons alone need no engraving option).
export function hasMultiPhotoText(value) {
  return filledMultiPhotoItems(value).some((entry) => entry.lines.length > 0);
}

// Quantity of one addon over all photos.
export function multiPhotoAddonTotal(entries, code) {
  return (entries || []).reduce(
    (sum, entry) => sum + (parseInt(entry.addons && entry.addons[code], 10) || 0),
    0
  );
}

// JSON for the cart item, or null when nothing was typed and no addon chosen
// (a canvas of a photo is kept too).
export function multiPhotoItemsToJson(entries) {
  let hasCanvas = parseMultiPhotoItems(entries).some((entry) => entry.canvas_project_id);
  return hasMultiPhotoContent(entries) || hasCanvas ? JSON.stringify(entries) : null;
}

// Bundle option chips of one "Photo k of N" row in the upload lists: the shared
// options of the item without the per photo ones (photo addons, options given per
// photo, customer text, the gift note when photos have their own), plus what
// belongs to this photo only.
export function multiPhotoRowBundleOptions(bundleOptions, value, index) {
  let filled = filledMultiPhotoItems(value);
  let perPhoto = {};
  filled.forEach((item) => {
    item.addons.forEach((addon) => {
      perPhoto[addon.code] = true;
    });
    if (item.giftNote) {
      perPhoto.laser_engraved_text = true;
    }
  });
  let options = {};
  Object.keys(bundleOptions || {}).forEach((key) => {
    let label = bundleOptions[key] && bundleOptions[key].label;
    if (label === "customer_text" || PHOTO_ADDON_LABELS[label] || perPhoto[label]) {
      return;
    }
    options[key] = bundleOptions[key];
  });
  let entry = filled.find((item) => item.photo === index + 1);
  if (!entry) {
    return options;
  }
  if (entry.lines.length) {
    options.multi_photo_text = {
      label: "customer_text",
      qtyBased: false,
      value: { title: "Text", text: entry.lines.join(" / ") },
    };
  }
  if (entry.giftNote) {
    options.multi_photo_gift_note = {
      label: "laser_engraved_text",
      qtyBased: false,
      value: { title: "Magnetic Gift", text: entry.giftNote },
    };
  }
  entry.addons.forEach((addon) => {
    options[`multi_photo_addon_${addon.code}`] = {
      label: addon.code,
      qtyBased: true,
      value: { title: addon.label, qty: addon.qty },
    };
  });
  return options;
}
